import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import { AppAlert, AppButton, AppConfirmModal, AppEmptyState, AppInput, AppSpinner } from '../../components/ui';
import { ROUTES } from '../../config/routes';
import agendaDatesService from '../../services/agendaDatesService';
import { eventDateParts, formatSaisie, matchPrix, parseDay, prixLabel } from './agendaDisplay';
import '../../styles/sites-web.scss';
import '../../styles/agenda-dates.scss';

const PAGE_SIZE = 10;

const PERIODS = [
  ['all', 'Toutes'],
  ['today', 'Aujourd’hui'],
  ['weekend', 'Ce week-end'],
  ['week', 'Cette semaine'],
  ['next7', '7 prochains jours'],
  ['month', 'Ce mois-ci'],
  ['nextmonth', 'Mois prochain'],
  ['custom', 'Personnalisée…'],
];

const PRICES = [
  ['all', 'Tous'],
  ['free', 'Gratuit'],
  ['paid', 'Payant'],
  ['libre', 'Prix libre'],
  ['max', 'Jusqu’à…'],
];

const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const iso = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const periodDates = (period, customFrom, customTo) => {
  const today = startOfDay(new Date());
  if (period === 'today') return { from: today, to: today };
  if (period === 'weekend') {
    const mondayIndex = (today.getDay() + 6) % 7;
    return { from: addDays(today, 5 - mondayIndex), to: addDays(today, 6 - mondayIndex) };
  }
  if (period === 'week') {
    const mondayIndex = (today.getDay() + 6) % 7;
    return { from: today, to: addDays(today, 6 - mondayIndex) };
  }
  if (period === 'next7') return { from: today, to: addDays(today, 6) };
  if (period === 'month') {
    return {
      from: new Date(today.getFullYear(), today.getMonth(), 1),
      to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
    };
  }
  if (period === 'nextmonth') {
    return {
      from: new Date(today.getFullYear(), today.getMonth() + 1, 1),
      to: new Date(today.getFullYear(), today.getMonth() + 2, 0),
    };
  }
  if (period === 'custom') {
    const from = customFrom ? parseDay(customFrom) : null;
    const to = customTo ? parseDay(customTo) : null;
    if (!from && !to) return null;
    return { from, to };
  }
  return null;
};

const overlaps = (item, range) => {
  if (!range) return true;
  const start = parseDay(item.dateDeDebut);
  const end = parseDay(item.dateDeFin) || start;
  if (!start || !end) return false;
  if (range.from && end < range.from) return false;
  if (range.to && start > range.to) return false;
  return true;
};

const AgendaDatesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queue = location.pathname.startsWith(ROUTES.agenda.datesAActiver);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [period, setPeriod] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [price, setPrice] = useState('all');
  const [prixMax, setPrixMax] = useState('');
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [server, setServer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [query, period, customFrom, customTo, tab, queue, price, prixMax]);

  useEffect(() => {
    let cancelled = false;
    const range = periodDates(period, customFrom, customTo);
    const vue = queue ? 'a_activer' : 'en_cours';
    setLoading(true);
    setError('');
    const prix = price === 'all' || (price === 'max' && prixMax === '') ? '' : price;
    agendaDatesService.list({
      vue,
      q: query,
      page,
      perPage: PAGE_SIZE,
      from: range ? iso(range.from) : '',
      to: range ? iso(range.to) : '',
      tab: queue ? '' : tab,
      prix,
      prixMax: price === 'max' ? prixMax : '',
    })
      .then(async (data) => {
        if (cancelled) return;
        const priceApplied = data?.filters && Object.prototype.hasOwnProperty.call(data.filters, 'prix');
        if (data?.counts && (prix === '' || priceApplied)) {
          setServer(true);
          setItems(data.items || []);
          setMeta(data);
          return;
        }
        const wide = await agendaDatesService.list({
          vue,
          q: query,
          page: 1,
          perPage: 100,
          from: range ? iso(range.from) : '',
          to: range ? iso(range.to) : '',
          tab: queue ? '' : tab,
        });
        if (cancelled) return;
        setServer(false);
        setItems(wide.items || []);
        setMeta(wide);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Chargement impossible.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queue, query, page, period, customFrom, customTo, tab, price, prixMax, reloadKey]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError('');
    try {
      await agendaDatesService.update(pendingDelete.id, { statut: 99 });
      setPendingDelete(null);
      setReloadKey((current) => current + 1);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
    } finally {
      setDeleting(false);
    }
  };

  const range = useMemo(
    () => periodDates(period, customFrom, customTo),
    [period, customFrom, customTo],
  );

  const rangeLabel = useMemo(() => {
    if (period === 'custom') return 'Effacer';
    if (!range?.from) return '';
    const short = (date) => {
      const year = date.getFullYear() === new Date().getFullYear() ? '' : ` ${date.getFullYear()}`;
      return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}${year}`;
    };
    const end = range.to || range.from;
    return range.from.getTime() === end.getTime() ? short(range.from) : `${short(range.from)} → ${short(end)}`;
  }, [period, range]);

  const filtered = useMemo(() => {
    if (server) return items;
    return items.filter((item) => overlaps(item, range)
      && matchPrix(item, price, prixMax)
      && (queue || tab === 'all' || (tab === 'on' ? item.statut === 1 : item.statut === 0)));
  }, [server, items, range, queue, tab, price, prixMax]);

  const pageItems = server ? items : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total = server ? Number(meta?.total || 0) : filtered.length;
  const pages = Math.max(1, server ? Number(meta?.pages || 1) : Math.ceil(total / PAGE_SIZE));
  const counts = server && meta?.counts
    ? meta.counts
    : {
      all: items.filter((item) => overlaps(item, range) && matchPrix(item, price, prixMax)).length,
      on: items.filter((item) => overlaps(item, range) && matchPrix(item, price, prixMax) && item.statut === 1).length,
      off: items.filter((item) => overlaps(item, range) && matchPrix(item, price, prixMax) && item.statut === 0).length,
    };
  const fromIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toIndex = Math.min(page * PAGE_SIZE, total);

  return (
    <MasterLayout>
      <div className="sites-web agenda-dates">
        <header className="sw-head">
          <div className="sw-head__titles">
            <h1 className="fw-semibold mb-0 app-page-title">{queue ? 'Dates à activer' : 'Dates'}</h1>
            <p>
              {queue
                ? 'Dates à venir encore désactivées, hors récurrences.'
                : 'Dates à venir, publiées ou encore à activer.'}
            </p>
          </div>
          {queue ? null : (
            <AppButton as={Link} to={ROUTES.agenda.dateNew} className="sw-btn sw-btn--dark" icon="lucide:plus" iconWidth={16}>
              Ajouter une date
            </AppButton>
          )}
        </header>

        {error ? <AppAlert variant="danger">{error}</AppAlert> : null}

        <div className="ag-tools">
          <div className="ag-filter-row">
            <label className="ag-search">
              <Icon icon="lucide:search" width={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Titre, lieu, adresse, commune ou n°"
              />
              {search ? (
                <button type="button" className="ag-clear" onClick={() => setSearch('')} aria-label="Effacer la recherche">
                  <Icon icon="lucide:x" width={14} />
                </button>
              ) : null}
            </label>

            {queue ? null : (
              <div className="ag-tabs" role="tablist" aria-label="Statut">
                {[
                  ['all', 'Toutes', counts.all],
                  ['on', 'Publiées', counts.on],
                  ['off', 'Désactivées', counts.off],
                ].map(([value, label, count]) => (
                  <button
                    key={value}
                    type="button"
                    className={`ag-tab${tab === value ? ' is-on' : ''}`}
                    onClick={() => setTab(value)}
                  >
                    {label}
                    <span className="ag-count">{count ?? 0}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ag-periods">
            <span className="ag-period-label">
              <Icon icon="lucide:calendar-range" width={15} />
              Période
            </span>
            {PERIODS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`ag-chip${period === value ? ' is-on' : ''}`}
                onClick={() => setPeriod(value)}
              >
                {label}
              </button>
            ))}
            {period === 'custom' ? (
              <span className="ag-custom">
                <label>
                  du
                  <AppInput type="date" value={customFrom} aria-label="Du" onChange={(event) => setCustomFrom(event.target.value)} />
                </label>
                <label>
                  au
                  <AppInput type="date" value={customTo} aria-label="Au" onChange={(event) => setCustomTo(event.target.value)} />
                </label>
              </span>
            ) : null}
            {period !== 'all' ? (
              <button
                type="button"
                className="ag-clear-period"
                onClick={() => {
                  setPeriod('all');
                  setCustomFrom('');
                  setCustomTo('');
                }}
              >
                <Icon icon="lucide:x" width={13} />
                {rangeLabel}
              </button>
            ) : null}
          </div>

          <div className="ag-periods">
            <span className="ag-period-label">
              <Icon icon="lucide:ticket" width={15} />
              Prix
            </span>
            {PRICES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`ag-chip${price === value ? ' is-on' : ''}`}
                onClick={() => setPrice(value)}
              >
                {label}
              </button>
            ))}
            {price === 'max' ? (
              <label className="ag-custom">
                <AppInput
                  type="number"
                  min="0"
                  step="1"
                  value={prixMax}
                  aria-label="Prix maximum"
                  placeholder="€"
                  onChange={(event) => setPrixMax(event.target.value)}
                />
              </label>
            ) : null}
            {price !== 'all' ? (
              <button
                type="button"
                className="ag-clear-period"
                onClick={() => {
                  setPrice('all');
                  setPrixMax('');
                }}
              >
                <Icon icon="lucide:x" width={13} />
                {price === 'free' ? 'Gratuit' : price === 'paid' ? 'Payant' : price === 'libre' ? 'Prix libre' : (prixMax ? `Jusqu’à ${prixMax} €` : 'Effacer')}
              </button>
            ) : null}
          </div>
        </div>

        <section className="ag-table">
          <div className="ag-head">
            <span>N°</span>
            <span>Titre</span>
            <span>Date</span>
            <span>Prix</span>
            <span>Adresse</span>
            <span>Provenance</span>
            <span />
          </div>
          {loading && pageItems.length === 0 ? (
            <div className="ag-empty"><AppSpinner /></div>
          ) : null}
          {!loading && pageItems.length === 0 ? (
            <div className="ag-empty">
              <AppEmptyState message="Aucune date" description="Modifiez la recherche, la période ou le prix." icon="lucide:calendar" />
            </div>
          ) : null}
          {pageItems.map((item) => {
            const when = eventDateParts(item);
            const price = prixLabel(item.prixMinimum, item.prixMaximum);
            const priceClass = price.tone === 'free' ? 'is-free' : price.tone === 'muted' ? 'is-muted' : '';
            const city = [item.commune?.codePostal, item.commune?.nom].filter(Boolean).join(' ');
            const editUrl = queue ? `${ROUTES.agenda.dateEdit(item.id)}?from=a-activer` : ROUTES.agenda.dateEdit(item.id);
            return (
              <article
                key={item.id}
                className="ag-row"
                onClick={() => navigate(editUrl)}
              >
                <div className="ag-id">
                  <strong>{item.id}</strong>
                  <span>{formatSaisie(item.dateSaisie)}</span>
                </div>
                <div className="ag-title">
                  <strong>{item.titre || 'Sans titre'}</strong>
                  <div className="ag-badges">
                    {Number(item.statut) === 0 ? <span className="ag-badge ag-badge--off">Désactivée</span> : null}
                    {when.ongoing ? <span className="ag-badge ag-badge--live">En cours</span> : null}
                    {item.recurrent ? <span className="ag-badge ag-badge--rec">Récurrente</span> : null}
                  </div>
                  {queue && item.missing?.length ? <span className="ag-miss">Il manque : {item.missing.join(', ')}</span> : null}
                </div>
                <div className="ag-when">
                  <strong>{when.main}</strong>
                  {when.sub ? <small>{when.sub}</small> : null}
                </div>
                <div className={`ag-prix ${priceClass}`}>{price.text}</div>
                <div className="ag-where">
                  <strong>{item.adresse || '—'}</strong>
                  {city ? <small>{city}</small> : null}
                </div>
                <div className="ag-from">{item.sourceImport || 'Saisie'}</div>
                <div className="ag-actions" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                  {item.publicUrl ? (
                    <AppButton
                      className="ag-iconbtn"
                      variant="secondary"
                      icon="lucide:eye"
                      iconWidth={16}
                      aria-label="Voir sur le site"
                      onClick={() => window.open(item.publicUrl, '_blank', 'noopener')}
                    />
                  ) : null}
                  <AppButton
                    as={Link}
                    to={editUrl}
                    className="ag-iconbtn"
                    variant="secondary"
                    icon="lucide:pencil"
                    iconWidth={16}
                    aria-label="Modifier"
                  />
                  <AppButton
                    className="ag-iconbtn ag-iconbtn--danger"
                    variant="secondary"
                    icon="lucide:trash-2"
                    iconWidth={16}
                    aria-label="Supprimer"
                    onClick={() => setPendingDelete(item)}
                  />
                </div>
              </article>
            );
          })}
          <footer className="ag-foot">
            <span>{total === 0 ? 'Aucune date' : `${total} date${total > 1 ? 's' : ''} · ${fromIndex}–${toIndex}`}</span>
            <div className="ag-pager">
              <AppButton className="sw-btn sw-btn--page ag-page" variant="secondary" icon="lucide:chevron-left" iconWidth={16} aria-label="Page précédente" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} />
              <AppButton className="sw-btn sw-btn--page ag-page" variant="secondary" icon="lucide:chevron-right" iconWidth={16} aria-label="Page suivante" disabled={page >= pages} onClick={() => setPage((current) => current + 1)} />
            </div>
          </footer>
        </section>
        <AppConfirmModal
          show={Boolean(pendingDelete)}
          className="agenda-dates"
          title="Supprimer cette date ?"
          confirmLabel="Supprimer"
          confirmVariant="danger"
          confirmIcon="lucide:trash-2"
          saving={deleting}
          savingLabel="Suppression…"
          onClose={() => {
            if (!deleting) setPendingDelete(null);
          }}
          onConfirm={confirmDelete}
        >
          <p className="mb-0">
            <strong>{pendingDelete?.titre || 'Sans titre'}</strong>
            {pendingDelete ? ` (n°${pendingDelete.id})` : ''} sera retirée de la liste.
          </p>
        </AppConfirmModal>
      </div>
    </MasterLayout>
  );
};

export default AgendaDatesPage;
